import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useJsApiLoader,
  GoogleMap,
  MarkerF,
  CircleF,
  PolylineF,
  StandaloneSearchBox,
  OverlayViewF,
  FLOAT_PANE,
} from '@react-google-maps/api';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import {
  addLocation,
  setLocationProp,
} from '../../../store/redux/slices/template.slice';
import { CustomSearchIcon } from '../../../theme/icons/CustomSearchIcon';
import {
  customPinIconDataUrl,
  MAP_PIN_HEAD_CENTER_VIEWBOX,
  MAP_PIN_VIEWBOX,
  MAP_PIN_ANCHOR_NUDGE_UP_PX,
} from '../../../theme/icons/CustomPinIcon';
import { Box, Divider, Flex, Text } from '@chakra-ui/react';
import CustomMapNavIcon from '../../../theme/icons/CustomMapNavIcon';
import CustomWarningIcon from '../../../theme/icons/CustomWarningIcon';
import { locationHasPlacedPin } from './utils/locationUtils';
import {
  DEFAULT_CENTER,
  FIT_BOUNDS_MAX_ZOOM,
  FIT_BOUNDS_PADDING,
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_MAP_ID,
  GOOGLE_WALLET_GEOFENCE_RADIUS_M,
  APPLE_RADIUS_RESIZE_HIT_STROKE_PX,
  APPLE_RADIUS_RESIZE_PIN_EXCLUSION_M,
  INACTIVE_MARKER_SCALE,
  MAP_CONTAINER_STYLE,
  MARKER_HOVER_PEER_OPACITY,
  MARKER_SCALED_HEIGHT,
  MARKER_SCALED_WIDTH,
  SEARCH_BOX_BORDER_RADIUS,
  libraries,
} from './utils/searchableMapConstants';
import {
  circlePath,
  clientXYToLatLngFromMapBounds,
  distanceBetweenPointsMeters,
  parseLocationCoord,
  placedGeofencesOverlap,
  pointOnCircleBorder,
} from './utils/searchableMapGeo';
import { snapAppleRadiusMeters } from './utils/appleRadiusLimits';
import './googlePlacesDropdown.css';

interface SearchableMapProps {
  locationIndex: number;
  searchResetKey?: number;
  expandedLocationId?: string | null;
  onSelectLocationById?: (locationId: string) => void;
  onPlacementHintVisibleChange?: (visible: boolean) => void;
  previewOnly?: boolean;
  pendingPlacement?: boolean;
  onPendingPlacementComplete?: () => void;
}

const SearchableMap = ({
  locationIndex,
  searchResetKey = 0,
  expandedLocationId = null,
  onSelectLocationById,
  onPlacementHintVisibleChange,
  previewOnly = false,
  pendingPlacement = false,
  onPendingPlacementComplete,
}: SearchableMapProps) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const skipNextMarkerClickRef = useRef(false);
  const searchBoxRef = useRef<google.maps.places.SearchBox | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const searchBarOverlayRef = useRef<HTMLDivElement | null>(null);
  const placesDropdownSyncRafRef = useRef<number>(0);
  const placesDropdownPollRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const prevSearchResetKeyRef = useRef(searchResetKey);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapHovered, setMapHovered] = useState(false);
  const [hoveredMarkerLocationId, setHoveredMarkerLocationId] = useState<
    string | null
  >(null);
  const [appleRadiusHover, setAppleRadiusHover] = useState<{
    locationId: string;
    radiusM: number;
    labelPosition: { lat: number; lng: number };
    hintPosition: { lat: number; lng: number };
  } | null>(null);
  const [markerDragPosition, setMarkerDragPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [googleRadiusFixedHint, setGoogleRadiusFixedHint] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const appleRadiusHoverClearTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const appleRadiusResizeDragRef = useRef<{
    locationIndex: number;
    centerLat: number;
    centerLng: number;
  } | null>(null);
  const appleRadiusResizeMoveListenerRef =
    useRef<google.maps.MapsEventListener | null>(null);
  const appleRadiusResizeUpListenerRef =
    useRef<google.maps.MapsEventListener | null>(null);
  const appleRadiusResizeSessionCleanupRef = useRef<(() => void) | null>(null);
  const peerHoverClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const googleFixedHintClearTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const googleFixedHintSessionCleanupRef = useRef<(() => void) | null>(null);

  const cancelPeerHoverClear = useCallback(() => {
    if (peerHoverClearTimerRef.current) {
      clearTimeout(peerHoverClearTimerRef.current);
      peerHoverClearTimerRef.current = null;
    }
  }, []);

  const schedulePeerHoverClear = useCallback(() => {
    cancelPeerHoverClear();
    peerHoverClearTimerRef.current = setTimeout(() => {
      setHoveredMarkerLocationId(null);
      peerHoverClearTimerRef.current = null;
    }, 150);
  }, [cancelPeerHoverClear]);

  const cancelAppleRadiusHoverClear = useCallback(() => {
    if (appleRadiusHoverClearTimerRef.current) {
      clearTimeout(appleRadiusHoverClearTimerRef.current);
      appleRadiusHoverClearTimerRef.current = null;
    }
  }, []);

  const scheduleAppleRadiusHoverClear = useCallback(() => {
    if (previewOnly) return;
    cancelAppleRadiusHoverClear();
    appleRadiusHoverClearTimerRef.current = setTimeout(() => {
      setAppleRadiusHover(null);
      appleRadiusHoverClearTimerRef.current = null;
    }, 150);
  }, [cancelAppleRadiusHoverClear, previewOnly]);

  useEffect(() => {
    return () => {
      cancelAppleRadiusHoverClear();
      cancelPeerHoverClear();
      googleFixedHintSessionCleanupRef.current?.();
      googleFixedHintSessionCleanupRef.current = null;
      if (googleFixedHintClearTimerRef.current) {
        clearTimeout(googleFixedHintClearTimerRef.current);
        googleFixedHintClearTimerRef.current = null;
      }
    };
  }, [cancelAppleRadiusHoverClear, cancelPeerHoverClear]);

  useEffect(() => {
    if (previewOnly) {
      cancelAppleRadiusHoverClear();
      setAppleRadiusHover(null);
      googleFixedHintSessionCleanupRef.current?.();
      googleFixedHintSessionCleanupRef.current = null;
      if (googleFixedHintClearTimerRef.current) {
        clearTimeout(googleFixedHintClearTimerRef.current);
        googleFixedHintClearTimerRef.current = null;
      }
      setGoogleRadiusFixedHint(null);
    }
  }, [previewOnly, cancelAppleRadiusHoverClear]);

  const clearGoogleFixedHintSession = useCallback(() => {
    googleFixedHintSessionCleanupRef.current?.();
    googleFixedHintSessionCleanupRef.current = null;
    if (googleFixedHintClearTimerRef.current) {
      clearTimeout(googleFixedHintClearTimerRef.current);
      googleFixedHintClearTimerRef.current = null;
    }
  }, []);

  const beginGoogleFixedRadiusHintDragWatch = useCallback(
    (
      e: google.maps.MapMouseEvent,
      centerLat: number,
      centerLng: number,
      radiusM: number
    ) => {
      if (previewOnly) return;
      const dom = e.domEvent as MouseEvent | undefined;
      if (dom) dom.stopPropagation();
      if (dom && typeof dom.button === 'number' && dom.button !== 0) return;
      if (!dom || typeof dom.clientX !== 'number') return;

      clearGoogleFixedHintSession();
      setGoogleRadiusFixedHint(null);

      const startX = dom.clientX;
      const startY = dom.clientY;
      let shown = false;

      const onMove = (ev: MouseEvent) => {
        if (shown) return;
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        if (dx * dx + dy * dy < 36) return;
        // North (angle 0) = top of circle in map coordinates — matches circlePath / pointOnCircleBorder
        setGoogleRadiusFixedHint(
          pointOnCircleBorder(centerLat, centerLng, radiusM, 0)
        );
        shown = true;
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp, true);
        googleFixedHintSessionCleanupRef.current = null;
        googleFixedHintClearTimerRef.current = setTimeout(() => {
          setGoogleRadiusFixedHint(null);
          googleFixedHintClearTimerRef.current = null;
        }, 2200);
      };

      googleFixedHintSessionCleanupRef.current = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp, true);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp, { capture: true });
    },
    [previewOnly, clearGoogleFixedHintSession]
  );

  const dispatch = useAppDispatch();
  const allLocations = useAppSelector((s) => s.template.locations);
  const templateType = useAppSelector((s) => s.template.templateType);
  const clampedEditIndex =
    pendingPlacement && allLocations.length > 0
      ? allLocations.length
      : allLocations.length === 0
        ? 0
        : Math.min(Math.max(0, locationIndex), allLocations.length - 1);
  useEffect(() => {
    if (
      searchResetKey > 0 &&
      searchResetKey !== prevSearchResetKeyRef.current
    ) {
      if (searchInputRef.current) searchInputRef.current.value = '';
    }
    prevSearchResetKeyRef.current = searchResetKey;
  }, [searchResetKey]);

  const placedLocations = useMemo(() => {
    const result: { loc: (typeof allLocations)[number]; index: number }[] = [];
    allLocations.forEach((loc, index) => {
      const lat = parseLocationCoord(loc.latitude);
      const lng = parseLocationCoord(loc.longitude);
      if (lat == null || lng == null) return;
      if (lat === 0 || lng === 0) return;
      result.push({
        loc: { ...loc, latitude: lat, longitude: lng },
        index,
      });
    });
    return result;
  }, [allLocations]);

  const placedBoundsKey = useMemo(
    () =>
      placedLocations
        .map(
          ({ loc }) =>
            `${Number(loc.latitude).toFixed(6)},${Number(loc.longitude).toFixed(6)}`
        )
        .join('|'),
    [placedLocations]
  );

  useEffect(() => {
    cancelAppleRadiusHoverClear();
    setAppleRadiusHover(null);
  }, [placedBoundsKey, cancelAppleRadiusHoverClear]);

  const hasOverlappingGeofences = useMemo(
    () =>
      placedGeofencesOverlap(
        placedLocations.map(({ loc }) => ({
          latitude: loc.latitude,
          longitude: loc.longitude,
          radius: loc.radius,
        }))
      ),
    [placedLocations]
  );

  const expandedLocation = useMemo(() => {
    if (!expandedLocationId) return null;
    return allLocations.find((l) => l._id === expandedLocationId) ?? null;
  }, [expandedLocationId, allLocations]);

  const expandedCoordsKey = useMemo(() => {
    const loc = expandedLocation;
    if (!loc) return '';
    const lat = parseLocationCoord(loc.latitude);
    const lng = parseLocationCoord(loc.longitude);
    if (lat == null || lng == null || lat === 0 || lng === 0) return '';
    return `${lat.toFixed(6)},${lng.toFixed(6)}`;
  }, [expandedLocation]);

  useEffect(() => {
    const expandedHasPinOnMap =
      expandedLocationId != null && expandedCoordsKey !== '';
    if (expandedHasPinOnMap) {
      cancelPeerHoverClear();
      setHoveredMarkerLocationId(null);
    }
  }, [expandedLocationId, expandedCoordsKey, cancelPeerHoverClear]);

  const editingRowHasPlacedMarker = useMemo(() => {
    if (pendingPlacement) return false;
    if (allLocations.length === 0) return false;
    const loc = allLocations[clampedEditIndex];
    if (!loc) return false;
    const lat = parseLocationCoord(loc.latitude);
    const lng = parseLocationCoord(loc.longitude);
    return lat != null && lng != null && lat !== 0 && lng !== 0;
  }, [allLocations, clampedEditIndex, pendingPlacement]);

  const applyMapPanCursorPreference = useCallback(
    (map: google.maps.Map | null) => {
      if (!map) return;
      if (!previewOnly && !editingRowHasPlacedMarker) {
        map.setOptions({
          draggableCursor: 'default',
          draggingCursor: 'grabbing',
        });
      } else {
        map.setOptions({
          draggableCursor: null,
          draggingCursor: null,
        });
      }
    },
    [previewOnly, editingRowHasPlacedMarker]
  );

  const editingLocation =
    !pendingPlacement && allLocations.length > 0
      ? allLocations[clampedEditIndex]
      : undefined;
  const editingAddress = editingLocation?.address?.trim() ?? '';

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || '',
    libraries,
  });

  const mapInitialView = useMemo(() => {
    if (!isLoaded || typeof google === 'undefined') {
      return { center: DEFAULT_CENTER, zoom: 9 };
    }
    const n = placedLocations.length;
    if (n === 0) {
      return { center: DEFAULT_CENTER, zoom: 9 };
    }
    if (n === 1) {
      const { loc } = placedLocations[0];
      return {
        center: { lat: loc.latitude, lng: loc.longitude },
        zoom: 16,
      };
    }
    const bounds = new google.maps.LatLngBounds();
    placedLocations.forEach(({ loc }) =>
      bounds.extend({ lat: loc.latitude, lng: loc.longitude })
    );
    const c = bounds.getCenter();
    return {
      center: { lat: c.lat(), lng: c.lng() },
      zoom: 12,
    };
  }, [isLoaded, placedBoundsKey, placedLocations]);

  useEffect(() => {
    if (previewOnly) return;
    if (pendingPlacement) return;
    if (!searchInputRef.current) return;
    searchInputRef.current.value = editingAddress;
  }, [
    previewOnly,
    pendingPlacement,
    clampedEditIndex,
    editingLocation?._id,
    editingAddress,
    isLoaded,
  ]);

  const reverseGeocode = useCallback(
    async (lat: number, lng: number): Promise<string> => {
      if (!geocoderRef.current) return '';
      try {
        const result = await geocoderRef.current.geocode({
          location: { lat, lng },
        });
        return result.results[0]?.formatted_address ?? '';
      } catch {
        return '';
      }
    },
    []
  );

  const applyMapCamera = useCallback(
    (map: google.maps.Map): (() => void) | undefined => {
      if (typeof google === 'undefined') return undefined;

      if (expandedLocationId && expandedCoordsKey && expandedLocation) {
        const lat = parseLocationCoord(expandedLocation.latitude);
        const lng = parseLocationCoord(expandedLocation.longitude);
        if (lat != null && lng != null) {
          map.panTo({ lat, lng });
          map.setZoom(16);
        }
        return undefined;
      }

      if (placedLocations.length === 0) {
        map.setCenter(DEFAULT_CENTER);
        map.setZoom(9);
        return undefined;
      }
      if (placedLocations.length === 1) {
        const { loc } = placedLocations[0];
        map.setCenter({ lat: loc.latitude, lng: loc.longitude });
        map.setZoom(16);
        return undefined;
      }
      const bounds = new google.maps.LatLngBounds();
      placedLocations.forEach(({ loc }) =>
        bounds.extend({ lat: loc.latitude, lng: loc.longitude })
      );
      map.fitBounds(bounds, FIT_BOUNDS_PADDING);
      const listener = google.maps.event.addListenerOnce(map, 'idle', () => {
        const z = map.getZoom();
        if (z != null && z > FIT_BOUNDS_MAX_ZOOM) {
          map.setZoom(FIT_BOUNDS_MAX_ZOOM);
        }
      });
      return () => {
        google.maps.event.removeListener(listener);
      };
    },
    [expandedLocationId, expandedCoordsKey, expandedLocation, placedLocations]
  );

  const handleMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      geocoderRef.current = new google.maps.Geocoder();
      setMapReady(true);
      requestAnimationFrame(() => {
        applyMapCamera(map);
      });
    },
    [applyMapCamera]
  );

  useEffect(() => {
    if (!mapRef.current || !mapReady || typeof google === 'undefined') return;
    return applyMapCamera(mapRef.current);
  }, [mapReady, applyMapCamera, placedBoundsKey]);

  useEffect(() => {
    if (!mapRef.current || !mapReady) return;
    applyMapPanCursorPreference(mapRef.current);
  }, [mapReady, applyMapPanCursorPreference]);

  useEffect(() => {
    if (!mapRef.current || !mapReady) return;
    mapRef.current.setOptions({ draggable: true });
  }, [mapReady]);

  const mapOptions = useMemo<google.maps.MapOptions | undefined>(() => {
    if (!isLoaded || typeof google === 'undefined') return undefined;
    return {
      mapId: GOOGLE_MAPS_MAP_ID,
      draggable: true,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
      },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      keyboardShortcuts: false,
      cameraControl: false,
    };
  }, [isLoaded]);

  const endAppleRadiusResizeDrag = useCallback(() => {
    appleRadiusResizeSessionCleanupRef.current?.();
    appleRadiusResizeSessionCleanupRef.current = null;
    appleRadiusResizeDragRef.current = null;
    const map = mapRef.current;
    if (map) {
      map.setOptions({ draggable: true });
      applyMapPanCursorPreference(map);
    }
    if (typeof google !== 'undefined') {
      if (appleRadiusResizeMoveListenerRef.current) {
        google.maps.event.removeListener(
          appleRadiusResizeMoveListenerRef.current
        );
        appleRadiusResizeMoveListenerRef.current = null;
      }
      if (appleRadiusResizeUpListenerRef.current) {
        google.maps.event.removeListener(
          appleRadiusResizeUpListenerRef.current
        );
        appleRadiusResizeUpListenerRef.current = null;
      }
    }
  }, [applyMapPanCursorPreference]);

  const handleActiveAppleRadiusMouseDown = useCallback(
    (
      e: google.maps.MapMouseEvent,
      locIndex: number,
      centerLat: number,
      centerLng: number,
      currentRadiusM: number,
      fromHitRing?: boolean
    ) => {
      if (previewOnly || pendingPlacement) return;
      if (locIndex !== clampedEditIndex) return;
      const ll = e.latLng;
      if (!ll) return;
      const clickLat = ll.lat();
      const clickLng = ll.lng();
      const dist = distanceBetweenPointsMeters(
        centerLat,
        centerLng,
        clickLat,
        clickLng
      );

      const pinExcl = Math.min(
        APPLE_RADIUS_RESIZE_PIN_EXCLUSION_M,
        Math.max(12, currentRadiusM * 0.3)
      );
      if (dist < pinExcl) return;

      if (!fromHitRing) {
        const inner = currentRadiusM * 0.5;
        const outer = Math.max(currentRadiusM * 1.4, currentRadiusM + 48);
        if (dist < inner || dist > outer) return;
      }

      const dom = e.domEvent;
      if (dom) {
        dom.stopPropagation();
      }

      cancelAppleRadiusHoverClear();
      setAppleRadiusHover(null);
      clearGoogleFixedHintSession();
      setGoogleRadiusFixedHint(null);

      const map = mapRef.current;
      if (!map || typeof google === 'undefined') return;

      const mapDiv = map.getDiv();
      let didCleanup = false;
      let lastR = currentRadiusM;

      const onDomMove = (ev: MouseEvent) => {
        const drag = appleRadiusResizeDragRef.current;
        if (!drag) return;
        const latLng = clientXYToLatLngFromMapBounds(
          map,
          ev.clientX,
          ev.clientY
        );
        if (!latLng) return;
        const d = distanceBetweenPointsMeters(
          drag.centerLat,
          drag.centerLng,
          latLng.lat,
          latLng.lng
        );
        const next = snapAppleRadiusMeters(d, templateType);
        if (next !== lastR) {
          lastR = next;
          dispatch(
            setLocationProp({
              index: drag.locationIndex,
              field: 'radius',
              value: next,
            })
          );
        }
      };

      const cleanupSession = () => {
        if (didCleanup) return;
        didCleanup = true;
        mapDiv.removeEventListener('mousemove', onDomMove);
        window.removeEventListener('mouseup', onWinUp, { capture: true });
        appleRadiusResizeSessionCleanupRef.current = null;
        appleRadiusResizeDragRef.current = null;
        map.setOptions({ draggable: true });
        applyMapPanCursorPreference(map);
      };

      const onWinUp = () => {
        cleanupSession();
      };

      appleRadiusResizeSessionCleanupRef.current = cleanupSession;

      appleRadiusResizeDragRef.current = {
        locationIndex: locIndex,
        centerLat,
        centerLng,
      };
      map.setOptions({ draggable: false });

      mapDiv.addEventListener('mousemove', onDomMove);
      window.addEventListener('mouseup', onWinUp, { capture: true });
    },
    [
      previewOnly,
      pendingPlacement,
      clampedEditIndex,
      templateType,
      dispatch,
      cancelAppleRadiusHoverClear,
      applyMapPanCursorPreference,
      clearGoogleFixedHintSession,
    ]
  );

  useEffect(() => {
    return () => {
      endAppleRadiusResizeDrag();
    };
  }, [endAppleRadiusResizeDrag]);

  const handleMarkerDragStart = useCallback(() => {
    endAppleRadiusResizeDrag();
    cancelAppleRadiusHoverClear();
    setAppleRadiusHover(null);
    clearGoogleFixedHintSession();
    setGoogleRadiusFixedHint(null);
    setMarkerDragPosition(null);
  }, [
    cancelAppleRadiusHoverClear,
    clearGoogleFixedHintSession,
    endAppleRadiusResizeDrag,
  ]);

  const handleMarkerDrag = useCallback((e: google.maps.MapMouseEvent) => {
    const lat = e.latLng?.lat();
    const lng = e.latLng?.lng();
    if (lat == null || lng == null) return;
    setMarkerDragPosition({ lat, lng });
  }, []);

  const handleMarkerDragEnd = useCallback(
    async (e: google.maps.MapMouseEvent) => {
      endAppleRadiusResizeDrag();
      cancelAppleRadiusHoverClear();
      setAppleRadiusHover(null);
      skipNextMarkerClickRef.current = true;
      window.setTimeout(() => {
        skipNextMarkerClickRef.current = false;
      }, 100);
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat == null || lng == null) return;
      dispatch(
        setLocationProp({
          index: clampedEditIndex,
          field: 'latitude',
          value: lat,
        })
      );
      dispatch(
        setLocationProp({
          index: clampedEditIndex,
          field: 'longitude',
          value: lng,
        })
      );
      setMarkerDragPosition(null);
      const name = await reverseGeocode(lat, lng);
      if (name) {
        dispatch(
          setLocationProp({
            index: clampedEditIndex,
            field: 'address',
            value: name,
          })
        );
        if (searchInputRef.current) searchInputRef.current.value = name;
      }
    },
    [
      dispatch,
      clampedEditIndex,
      reverseGeocode,
      cancelAppleRadiusHoverClear,
      endAppleRadiusResizeDrag,
    ]
  );

  const handleMarkerClick = useCallback(
    (locationId: string | undefined) => {
      if (!locationId || !onSelectLocationById) return;
      if (skipNextMarkerClickRef.current) return;
      onSelectLocationById(locationId);
    },
    [onSelectLocationById]
  );

  const handlePlacesChanged = useCallback(() => {
    const places = searchBoxRef.current?.getPlaces?.();
    const place = places?.[0];
    const loc = place?.geometry?.location;
    if (!loc) return;
    const lat: number =
      typeof loc.lat === 'function' ? loc.lat() : Number(loc.lat);
    const lng: number =
      typeof loc.lng === 'function' ? loc.lng() : Number(loc.lng);
    const name = place?.formatted_address ?? place?.name ?? '';

    if (pendingPlacement) {
      dispatch(
        addLocation({
          latitude: lat,
          longitude: lng,
          address: name || undefined,
        })
      );
      onPendingPlacementComplete?.();
      return;
    }

    if (allLocations.length === 0) {
      dispatch(
        addLocation({
          latitude: lat,
          longitude: lng,
          address: name || undefined,
        })
      );
      return;
    }

    dispatch(
      setLocationProp({
        index: clampedEditIndex,
        field: 'latitude',
        value: lat,
      })
    );
    dispatch(
      setLocationProp({
        index: clampedEditIndex,
        field: 'longitude',
        value: lng,
      })
    );
    if (name) {
      dispatch(
        setLocationProp({
          index: clampedEditIndex,
          field: 'address',
          value: name,
        })
      );
    }
  }, [
    dispatch,
    pendingPlacement,
    onPendingPlacementComplete,
    clampedEditIndex,
    allLocations.length,
  ]);

  const handleMapClick = useCallback(
    async (e: google.maps.MapMouseEvent) => {
      if (previewOnly) return;
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat == null || lng == null) return;

      if (pendingPlacement) {
        const name = await reverseGeocode(lat, lng);
        dispatch(
          addLocation({
            latitude: lat,
            longitude: lng,
            address: name || undefined,
          })
        );
        if (name && searchInputRef.current) searchInputRef.current.value = name;
        onPendingPlacementComplete?.();
        return;
      }

      if (allLocations.length === 0) {
        const name = await reverseGeocode(lat, lng);
        dispatch(
          addLocation({
            latitude: lat,
            longitude: lng,
            address: name || undefined,
          })
        );
        if (name && searchInputRef.current) searchInputRef.current.value = name;
        return;
      }

      const loc = allLocations[clampedEditIndex];
      if (!loc) return;
      if (locationHasPlacedPin(loc)) return;

      dispatch(
        setLocationProp({
          index: clampedEditIndex,
          field: 'latitude',
          value: lat,
        })
      );
      dispatch(
        setLocationProp({
          index: clampedEditIndex,
          field: 'longitude',
          value: lng,
        })
      );
      const name = await reverseGeocode(lat, lng);
      if (name) {
        dispatch(
          setLocationProp({
            index: clampedEditIndex,
            field: 'address',
            value: name,
          })
        );
        if (searchInputRef.current) searchInputRef.current.value = name;
      }
    },
    [
      previewOnly,
      pendingPlacement,
      allLocations,
      clampedEditIndex,
      dispatch,
      reverseGeocode,
      onPendingPlacementComplete,
    ]
  );

  const handleSearchBoxLoad = useCallback(
    (searchBox: google.maps.places.SearchBox) => {
      searchBoxRef.current = searchBox;
    },
    []
  );

  const handleSearchBoxUnmount = useCallback(
    (searchBox: google.maps.places.SearchBox) => {
      searchBoxRef.current = null;
      if (typeof google !== 'undefined' && google.maps?.event) {
        google.maps.event.clearInstanceListeners(searchBox);
      }
    },
    []
  );

  const syncPlacesDropdownWidthToSearchBar = useCallback(() => {
    if (previewOnly) return;
    const bar = searchBarOverlayRef.current;
    if (!bar) return;

    const all = Array.from(
      document.querySelectorAll('.pac-container')
    ) as HTMLElement[];

    const visible = all.filter(
      (p) => p.isConnected && window.getComputedStyle(p).display !== 'none'
    );
    if (visible.length === 0) return;

    const target = visible[visible.length - 1];
    visible.slice(0, -1).forEach((p) => {
      p.style.setProperty('display', 'none', 'important');
      p.style.setProperty('visibility', 'hidden', 'important');
    });

    const { width, left } = bar.getBoundingClientRect();
    const w = Math.round(width);
    const l = Math.round(left);
    target.style.boxSizing = 'border-box';
    target.style.width = `${w}px`;
    target.style.minWidth = `${w}px`;
    target.style.maxWidth = `${w}px`;
    target.style.left = `${l}px`;
    target.style.right = 'auto';
  }, [previewOnly]);

  const scheduleSyncPlacesDropdownWidth = useCallback(() => {
    if (placesDropdownSyncRafRef.current) {
      cancelAnimationFrame(placesDropdownSyncRafRef.current);
    }
    placesDropdownSyncRafRef.current = requestAnimationFrame(() => {
      syncPlacesDropdownWidthToSearchBar();
      placesDropdownSyncRafRef.current = 0;
    });
  }, [syncPlacesDropdownWidthToSearchBar]);

  useEffect(() => {
    if (previewOnly || loadError || !isLoaded || !mapReady) return;
    const input = searchInputRef.current;
    if (!input) return;

    const stopPoll = () => {
      if (placesDropdownPollRef.current) {
        clearInterval(placesDropdownPollRef.current);
        placesDropdownPollRef.current = null;
      }
    };

    const startPoll = () => {
      if (placesDropdownPollRef.current) return;
      placesDropdownPollRef.current = setInterval(
        () => scheduleSyncPlacesDropdownWidth(),
        100
      );
    };

    const onFocus = () => {
      startPoll();
      scheduleSyncPlacesDropdownWidth();
    };
    const onBlur = () => {
      stopPoll();
    };

    input.addEventListener('focus', onFocus);
    input.addEventListener('blur', onBlur);
    input.addEventListener('input', scheduleSyncPlacesDropdownWidth);

    const onWin = () => scheduleSyncPlacesDropdownWidth();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);

    return () => {
      stopPoll();
      input.removeEventListener('focus', onFocus);
      input.removeEventListener('blur', onBlur);
      input.removeEventListener('input', scheduleSyncPlacesDropdownWidth);
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
      if (placesDropdownSyncRafRef.current) {
        cancelAnimationFrame(placesDropdownSyncRafRef.current);
        placesDropdownSyncRafRef.current = 0;
      }
    };
  }, [
    previewOnly,
    loadError,
    isLoaded,
    mapReady,
    scheduleSyncPlacesDropdownWidth,
  ]);

  const hidePlacementChromeUntilAdd =
    allLocations.length === 0 && !pendingPlacement;

  const placementHintVisible =
    !previewOnly &&
    !hidePlacementChromeUntilAdd &&
    isLoaded &&
    !loadError &&
    !editingRowHasPlacedMarker &&
    !mapHovered;

  useEffect(() => {
    onPlacementHintVisibleChange?.(placementHintVisible);
  }, [placementHintVisible, onPlacementHintVisibleChange]);

  if (loadError) {
    return (
      <div
        style={{
          ...MAP_CONTAINER_STYLE,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f5f5f5',
        }}
      >
        Failed to load map.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        style={{
          ...MAP_CONTAINER_STYLE,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f5f5f5',
        }}
      >
        Loading map...
      </div>
    );
  }

  const mapHoverBorder = '#E53E3E';
  const showMapHoverBorder =
    !previewOnly && !hidePlacementChromeUntilAdd && !editingRowHasPlacedMarker;
  const showPlacementHintOverlay =
    !previewOnly && !hidePlacementChromeUntilAdd && !editingRowHasPlacedMarker;

  const expandedSectionHasPlacedPin =
    expandedLocationId != null &&
    expandedCoordsKey !== '' &&
    (expandedLocation?.address?.trim() ?? '').length > 0;
  const showChangeLocationHintOverlay =
    !previewOnly &&
    !hidePlacementChromeUntilAdd &&
    !pendingPlacement &&
    expandedSectionHasPlacedPin &&
    !mapHovered &&
    !showPlacementHintOverlay;

  return (
    <>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: MAP_CONTAINER_STYLE.height,
          boxSizing: 'border-box',
          outline: showMapHoverBorder ? `2px solid ${mapHoverBorder}` : 'none',
          outlineOffset: '0px',
        }}
        onMouseEnter={() => !previewOnly && setMapHovered(true)}
        onMouseLeave={() => !previewOnly && setMapHovered(false)}
      >
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={mapInitialView.center}
          zoom={mapInitialView.zoom}
          onLoad={handleMapLoad}
          onClick={previewOnly ? undefined : handleMapClick}
          options={mapOptions}
        >
          {!previewOnly ? (
            <div
              style={{
                position: 'absolute',
                top: 10,
                left: 12,
                right: 10,
                zIndex: 10,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 12,
                pointerEvents: 'none',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 10,
                  backgroundColor: 'white',
                  padding: '5px 8px',
                  borderRadius: 8,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                  flexShrink: 0,
                  fontSize: 12,
                }}
                aria-label="Map legend"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: '#B2D7FF',
                      border: '2px solid #007AFF',
                      flexShrink: 0,
                    }}
                  />
                  <span>Apple radius</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: '#C2E5CB',
                      border: '2px dashed #34A754',
                      flexShrink: 0,
                    }}
                  />
                  <span>Google radius</span>
                </div>
              </div>
              <div
                ref={searchBarOverlayRef}
                style={{
                  flex: 1,
                  minWidth: 0,
                  backgroundColor: 'white',
                  padding: '4px 8px',
                  borderRadius: SEARCH_BOX_BORDER_RADIUS,
                }}
              >
                <StandaloneSearchBox
                  onLoad={handleSearchBoxLoad}
                  onUnmount={handleSearchBoxUnmount}
                  onPlacesChanged={handlePlacesChanged}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      pointerEvents: 'auto',
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        flexShrink: 0,
                        width: 20,
                        height: 20,
                        marginLeft: 10,
                        alignItems: 'center',
                        justifyContent: 'center',
                        transform: 'scale(0.77)',
                      }}
                      aria-hidden
                    >
                      <CustomSearchIcon boxSize={5} color="#666666" />
                    </span>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search..."
                      style={{
                        flex: 1,
                        minWidth: 0,
                        padding: '8px 12px 8px 0',
                        border: 'none',
                        outline: 'none',
                        borderRadius: SEARCH_BOX_BORDER_RADIUS,
                        fontSize: '14px',
                      }}
                    />
                  </div>
                </StandaloneSearchBox>
              </div>
            </div>
          ) : (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                backgroundColor: 'rgba(255, 255, 255, 0.24)',
                pointerEvents: 'none',
              }}
              aria-label="Preview only"
            >
              <div
                style={{
                  position: 'absolute',
                  top: 10,
                  left: 12,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  padding: '8px 12px',
                  borderRadius: 10,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#4C4C4C',
                }}
              >
                Preview only
              </div>
            </div>
          )}
          {placedLocations.map(({ loc, index: locIndex }) => {
            const expandedRowHasPin =
              expandedLocationId != null && expandedCoordsKey !== '';
            const isOpenSection =
              expandedRowHasPin && loc._id === expandedLocationId;
            const isInactivePin = expandedRowHasPin && !isOpenSection;
            const markerKey = loc._id ?? `idx-${locIndex}`;
            const dimOthersForHover =
              !expandedRowHasPin &&
              hoveredMarkerLocationId != null &&
              hoveredMarkerLocationId !== markerKey;
            const scale = isInactivePin ? INACTIVE_MARKER_SCALE : 1;
            const w = Math.round(MARKER_SCALED_WIDTH * scale);
            const h = Math.round(MARKER_SCALED_HEIGHT * scale);
            const nudge = Math.round(MAP_PIN_ANCHOR_NUDGE_UP_PX * scale);
            const markerOpacity = isInactivePin
              ? 0.6
              : dimOthersForHover
                ? MARKER_HOVER_PEER_OPACITY
                : 1;
            const markerPosition =
              locIndex === clampedEditIndex
                ? (markerDragPosition ?? {
                    lat: loc.latitude,
                    lng: loc.longitude,
                  })
                : { lat: loc.latitude, lng: loc.longitude };
            const isEditableMarkerDrag =
              !previewOnly &&
              !pendingPlacement &&
              locIndex === clampedEditIndex;
            return (
              <MarkerF
                key={`marker-${locIndex}`}
                position={markerPosition}
                draggable={isEditableMarkerDrag}
                onDragStart={
                  isEditableMarkerDrag ? handleMarkerDragStart : undefined
                }
                onDrag={isEditableMarkerDrag ? handleMarkerDrag : undefined}
                onDragEnd={
                  isEditableMarkerDrag ? handleMarkerDragEnd : undefined
                }
                clickable={!previewOnly && !pendingPlacement}
                onClick={
                  pendingPlacement
                    ? undefined
                    : () => handleMarkerClick(loc._id)
                }
                onMouseOver={
                  pendingPlacement || expandedRowHasPin
                    ? undefined
                    : () => {
                        cancelPeerHoverClear();
                        setHoveredMarkerLocationId(markerKey);
                      }
                }
                onMouseOut={
                  pendingPlacement || expandedRowHasPin
                    ? undefined
                    : () => schedulePeerHoverClear()
                }
                opacity={markerOpacity}
                zIndex={
                  isOpenSection
                    ? 10
                    : hoveredMarkerLocationId === markerKey
                      ? 5
                      : 1
                }
                icon={{
                  url: customPinIconDataUrl,
                  scaledSize: new google.maps.Size(w, h),
                  anchor: new google.maps.Point(
                    (MAP_PIN_HEAD_CENTER_VIEWBOX.x / MAP_PIN_VIEWBOX.width) * w,
                    (MAP_PIN_HEAD_CENTER_VIEWBOX.y / MAP_PIN_VIEWBOX.height) *
                      h +
                      nudge
                  ),
                }}
              />
            );
          })}
          {!previewOnly &&
            placedLocations.map(({ loc, index: locIndex }) => {
              const appleRadiusM = loc.radius ?? 80;
              const googleRadiusM = GOOGLE_WALLET_GEOFENCE_RADIUS_M;
              const appleAtMax100 = appleRadiusM === 100;
              const geofenceInteractive = !pendingPlacement;
              const expandedRowHasPin =
                expandedLocationId != null && expandedCoordsKey !== '';
              const isActiveGeofence =
                expandedRowHasPin && loc._id === expandedLocationId;
              const appleColor = isActiveGeofence ? '#0066D4' : '#007AFF';
              const googleColor = isActiveGeofence ? '#2C9147' : '#34A853';
              const appleFillOp = isActiveGeofence ? 0.3 : 0.22;
              const googleFillOp = isActiveGeofence ? 0.24 : 0.15;
              const dashedGreenOutline = (): google.maps.PolylineOptions => ({
                geodesic: true,
                strokeOpacity: 0,
                strokeWeight: 0,
                icons: [
                  {
                    icon: {
                      path: 'M 0,-1 0,1',
                      strokeColor: googleColor,
                      strokeOpacity: 0.85,
                      strokeWeight: 2,
                      scale: 2,
                    },
                    offset: '0',
                    repeat: '8px',
                  },
                ],
              });
              const locationKey = loc._id ?? `idx-${locIndex}`;
              const geofenceCenter =
                locIndex === clampedEditIndex && markerDragPosition != null
                  ? markerDragPosition
                  : { lat: loc.latitude, lng: loc.longitude };
              const canResizeAppleRadiusOnMap =
                isActiveGeofence &&
                !previewOnly &&
                !pendingPlacement &&
                locIndex === clampedEditIndex;
              const showAppleRadiusLabel = !previewOnly
                ? () => {
                    cancelAppleRadiusHoverClear();
                    setAppleRadiusHover({
                      locationId: locationKey,
                      radiusM: appleRadiusM,
                      labelPosition: pointOnCircleBorder(
                        geofenceCenter.lat,
                        geofenceCenter.lng,
                        appleRadiusM
                      ),
                      hintPosition: pointOnCircleBorder(
                        geofenceCenter.lat,
                        geofenceCenter.lng,
                        appleRadiusM,
                        Math.PI
                      ),
                    });
                  }
                : undefined;
              const hideAppleRadiusLabel = !previewOnly
                ? () => scheduleAppleRadiusHoverClear()
                : undefined;
              const onAppleGeofenceMouseOver = () => {
                if (!expandedRowHasPin) {
                  cancelPeerHoverClear();
                  setHoveredMarkerLocationId(locationKey);
                }
                showAppleRadiusLabel?.();
              };
              const onAppleGeofenceMouseOut = () => {
                if (!expandedRowHasPin) {
                  schedulePeerHoverClear();
                }
                hideAppleRadiusLabel?.();
              };
              const onGoogleGeofenceMouseOver = expandedRowHasPin
                ? undefined
                : () => {
                    cancelPeerHoverClear();
                    setHoveredMarkerLocationId(locationKey);
                  };
              const onGoogleGeofenceMouseOut = expandedRowHasPin
                ? undefined
                : () => schedulePeerHoverClear();
              const googleLayerClickable =
                geofenceInteractive && !canResizeAppleRadiusOnMap;
              const onGoogleGeofenceResizeAttempt = (
                e: google.maps.MapMouseEvent
              ) => {
                beginGoogleFixedRadiusHintDragWatch(
                  e,
                  geofenceCenter.lat,
                  geofenceCenter.lng,
                  googleRadiusM
                );
              };
              return (
                <Fragment key={`geofence-${loc._id ?? locIndex}`}>
                  <CircleF
                    center={{
                      lat: geofenceCenter.lat,
                      lng: geofenceCenter.lng,
                    }}
                    radius={googleRadiusM}
                    options={{
                      fillColor: googleColor,
                      fillOpacity: appleAtMax100 ? 0 : googleFillOp,
                      strokeColor: googleColor,
                      strokeOpacity: 0,
                      strokeWeight: 0,
                      clickable: googleLayerClickable,
                      zIndex: isActiveGeofence ? 1 : 0,
                    }}
                    onMouseOver={
                      googleLayerClickable
                        ? onGoogleGeofenceMouseOver
                        : undefined
                    }
                    onMouseOut={
                      googleLayerClickable
                        ? onGoogleGeofenceMouseOut
                        : undefined
                    }
                    onMouseDown={
                      !previewOnly &&
                      geofenceInteractive &&
                      googleLayerClickable
                        ? onGoogleGeofenceResizeAttempt
                        : undefined
                    }
                  />
                  <CircleF
                    center={{
                      lat: geofenceCenter.lat,
                      lng: geofenceCenter.lng,
                    }}
                    radius={appleRadiusM}
                    options={{
                      fillColor: appleColor,
                      fillOpacity: appleFillOp,
                      strokeColor: appleColor,
                      strokeOpacity: appleAtMax100 ? 0 : 0.75,
                      strokeWeight: appleAtMax100
                        ? 0
                        : isActiveGeofence
                          ? 2
                          : 1.5,
                      clickable: geofenceInteractive,
                      zIndex: isActiveGeofence ? 3 : 2,
                    }}
                    onMouseOver={
                      geofenceInteractive ? onAppleGeofenceMouseOver : undefined
                    }
                    onMouseOut={
                      geofenceInteractive ? onAppleGeofenceMouseOut : undefined
                    }
                    onMouseDown={
                      canResizeAppleRadiusOnMap
                        ? (e) =>
                            handleActiveAppleRadiusMouseDown(
                              e,
                              locIndex,
                              geofenceCenter.lat,
                              geofenceCenter.lng,
                              appleRadiusM,
                              false
                            )
                        : undefined
                    }
                  />
                  {appleAtMax100 ? (
                    <PolylineF
                      path={circlePath(
                        geofenceCenter.lat,
                        geofenceCenter.lng,
                        appleRadiusM
                      )}
                      options={{
                        ...dashedGreenOutline(),
                        clickable: geofenceInteractive,
                        zIndex: isActiveGeofence ? 4 : 3,
                      }}
                      onMouseOver={
                        geofenceInteractive
                          ? onAppleGeofenceMouseOver
                          : undefined
                      }
                      onMouseOut={
                        geofenceInteractive
                          ? onAppleGeofenceMouseOut
                          : undefined
                      }
                      onMouseDown={
                        canResizeAppleRadiusOnMap
                          ? (e) =>
                              handleActiveAppleRadiusMouseDown(
                                e,
                                locIndex,
                                geofenceCenter.lat,
                                geofenceCenter.lng,
                                appleRadiusM,
                                false
                              )
                          : undefined
                      }
                    />
                  ) : null}
                  {!appleAtMax100 ? (
                    <PolylineF
                      path={circlePath(
                        geofenceCenter.lat,
                        geofenceCenter.lng,
                        googleRadiusM
                      )}
                      options={{
                        ...dashedGreenOutline(),
                        clickable: googleLayerClickable,
                        zIndex: isActiveGeofence ? 2 : 1,
                      }}
                      onMouseOver={
                        googleLayerClickable
                          ? onGoogleGeofenceMouseOver
                          : undefined
                      }
                      onMouseOut={
                        googleLayerClickable
                          ? onGoogleGeofenceMouseOut
                          : undefined
                      }
                      onMouseDown={
                        !previewOnly &&
                        geofenceInteractive &&
                        googleLayerClickable
                          ? onGoogleGeofenceResizeAttempt
                          : undefined
                      }
                    />
                  ) : null}
                  {!previewOnly &&
                  geofenceInteractive &&
                  canResizeAppleRadiusOnMap ? (
                    <PolylineF
                      path={circlePath(
                        geofenceCenter.lat,
                        geofenceCenter.lng,
                        googleRadiusM
                      )}
                      options={{
                        geodesic: true,
                        strokeOpacity: 0,
                        strokeWeight: 28,
                        clickable: true,
                        zIndex: 40,
                      }}
                      onMouseDown={onGoogleGeofenceResizeAttempt}
                    />
                  ) : null}
                  {canResizeAppleRadiusOnMap ? (
                    <PolylineF
                      path={circlePath(
                        geofenceCenter.lat,
                        geofenceCenter.lng,
                        appleRadiusM
                      )}
                      options={{
                        geodesic: true,
                        strokeOpacity: 0,
                        strokeWeight: APPLE_RADIUS_RESIZE_HIT_STROKE_PX,
                        clickable: true,
                        zIndex: 50,
                      }}
                      onMouseOver={onAppleGeofenceMouseOver}
                      onMouseOut={onAppleGeofenceMouseOut}
                      onMouseDown={(e) =>
                        handleActiveAppleRadiusMouseDown(
                          e,
                          locIndex,
                          geofenceCenter.lat,
                          geofenceCenter.lng,
                          appleRadiusM,
                          true
                        )
                      }
                    />
                  ) : null}
                </Fragment>
              );
            })}
          {appleRadiusHover ? (
            <>
              <OverlayViewF
                position={appleRadiusHover.labelPosition}
                mapPaneName={FLOAT_PANE}
                zIndex={2}
                getPixelPositionOffset={(width, height) => ({
                  x: -width / 2,
                  y: -height / 2,
                })}
              >
                <Box
                  role="status"
                  aria-label={`Apple geofence radius ${appleRadiusHover.radiusM} metres`}
                  onMouseEnter={() => {
                    cancelAppleRadiusHoverClear();
                    cancelPeerHoverClear();
                  }}
                  onMouseLeave={() => {
                    scheduleAppleRadiusHoverClear();
                    schedulePeerHoverClear();
                  }}
                  pointerEvents="auto"
                  py="3px"
                  px="5px"
                  bg="appleBlue"
                  borderWidth="1px"
                  borderStyle="solid"
                  borderColor="rgba(255, 255, 255, 0.35)"
                  borderRadius="5px"
                  color="white"
                  boxShadow="0 1px 4px rgba(0, 0, 0, 0.16)"
                  textAlign="center"
                  fontFamily="inherit"
                >
                  <Text fontSize="10px" fontWeight="600" lineHeight={1.15}>
                    {appleRadiusHover.radiusM}m
                  </Text>
                </Box>
              </OverlayViewF>
              {!previewOnly &&
              expandedLocationId &&
              appleRadiusHover.locationId === expandedLocationId ? (
                <OverlayViewF
                  position={appleRadiusHover.hintPosition}
                  mapPaneName={FLOAT_PANE}
                  zIndex={3}
                  getPixelPositionOffset={(width, height) => ({
                    x: -width / 2,
                    y: -height / 2,
                  })}
                >
                  <Box
                    role="note"
                    aria-label="Drag the circle border to resize Apple geofence radius"
                    onMouseEnter={() => {
                      cancelAppleRadiusHoverClear();
                      cancelPeerHoverClear();
                    }}
                    onMouseLeave={() => {
                      scheduleAppleRadiusHoverClear();
                      schedulePeerHoverClear();
                    }}
                    pointerEvents="auto"
                    py="3px"
                    px="8px"
                    bg="appleBlue"
                    borderWidth="1px"
                    borderStyle="solid"
                    borderColor="rgba(255, 255, 255, 0.35)"
                    borderRadius="7px"
                    color="white"
                    boxShadow="0 2px 8px rgba(0, 0, 0, 0.2)"
                    textAlign="center"
                    fontFamily="inherit"
                  >
                    <Text fontSize="9.5px" fontWeight="500" lineHeight={1.25}>
                      Drag to resize
                    </Text>
                  </Box>
                </OverlayViewF>
              ) : null}
            </>
          ) : null}
          {googleRadiusFixedHint ? (
            <OverlayViewF
              position={googleRadiusFixedHint}
              mapPaneName={FLOAT_PANE}
              zIndex={4}
              getPixelPositionOffset={() => ({ x: 0, y: 0 })}
            >
              <Box
                role="status"
                aria-label="Google radius is fixed"
                pointerEvents="none"
                py="3px"
                px="6px"
                bg="#2C9147"
                borderWidth="1px"
                borderStyle="solid"
                borderColor="rgba(255, 255, 255, 0.35)"
                borderRadius="5px"
                color="white"
                boxShadow="0 1px 4px rgba(0, 0, 0, 0.16)"
                textAlign="center"
                fontFamily="inherit"
                whiteSpace="nowrap"
                boxSizing="border-box"
                sx={{
                  position: 'relative',
                  transform: 'translate(-50%, calc(-100% + 14px))',
                }}
              >
                <Text fontSize="10px" fontWeight="600" lineHeight={1.15}>
                  Google radius is fixed
                </Text>
              </Box>
            </OverlayViewF>
          ) : null}
        </GoogleMap>
        {showPlacementHintOverlay ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 8,
            }}
            aria-hidden
          >
            <div
              style={{
                maxWidth: '85%',
                padding: '8px 16px',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '8px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                fontSize: '12px',
                fontWeight: 600,
                color: '#FF4935',
                textAlign: 'center',
                lineHeight: 1.4,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                transform: 'translateY(-60px)',
              }}
            >
              <CustomMapNavIcon />
              Click the map or search for address
            </div>
          </div>
        ) : showChangeLocationHintOverlay ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 8,
            }}
            role="status"
            aria-live="polite"
          >
            <div
              style={{
                maxWidth: '85%',
                padding: '8px 16px',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '8px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                fontSize: '12px',
                fontWeight: 600,
                color: '#FF4935',
                textAlign: 'center',
                lineHeight: 1.4,
                transform: 'translateY(-60px)',
              }}
            >
              Search or drag pin to change this location
            </div>
          </div>
        ) : null}
      </div>
      {!previewOnly && hasOverlappingGeofences ? (
        <Flex
          w="100%"
          bg="#FFEEE7"
          borderRadius="5px"
          px={3}
          py={2}
          alignItems="center"
          gap={2}
        >
          <CustomWarningIcon boxSize={4} color="#FF5F15" />
          <Text
            fontSize="13px"
            color="#000000"
            lineHeight="1.5"
            fontWeight="500"
          >
            <b>Overlapping locations</b> may trigger multiple Wallet displays.
            Avoid overlap unless locations share the same message.
          </Text>
        </Flex>
      ) : null}
      <Divider borderColor="lightGrey" borderWidth="1px" />
    </>
  );
};

export default SearchableMap;
